import { FunctionComponent } from "react";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize, FontSize } from "@/components/Button/types";

import { SignalCard } from "./SignalCard";
import {
	ProspectSignalGroup as ProspectSignalGroupType,
	TrackerSignalResultAdditional,
} from "./combinedSignalFeedUtils";

interface ProspectSignalGroupProps {
	currentLeadIndex: number;
	expandedSummaryIds: Set<number>;
	group: ProspectSignalGroupType;
	isExpanded: boolean;
	setCardRef: (key: string) => (el: HTMLDivElement | null) => void;
	onGenerateDrafts?: (inboxIndex: number, signal: TrackerSignalResultAdditional) => void;
	onReachOut?: (inboxIndex: number, signal: TrackerSignalResultAdditional) => void;
	onSelectLead: (inboxIndex: number) => void;
	onToggleSignals: (inboxIndex: number) => void;
	onToggleSummary: (signalId: number) => void;
}

const getToggleSignalsLabel = (
	prospectName: string,
	remainingSignalCount: number,
	isExpanded: boolean
) => {
	if (isExpanded) {
		return `Hide signals of ${prospectName}`;
	}

	return `Show ${remainingSignalCount} more signal${remainingSignalCount > 1 ? "s" : ""} of ${prospectName}`;
};

export const ProspectSignalGroup: FunctionComponent<ProspectSignalGroupProps> = ({
	currentLeadIndex,
	expandedSummaryIds,
	group,
	isExpanded,
	setCardRef,
	onGenerateDrafts,
	onReachOut,
	onSelectLead,
	onToggleSignals,
	onToggleSummary,
}) => {
	const { inboxIndex, listPersonId, prospectId, prospectName, organizationName, signals } = group;
	const [primarySignal, ...additionalSignals] = signals;

	return (
		<div className="flex flex-col gap-2">
			<SignalCard
				cardKey={`${primarySignal.id}-${inboxIndex}`}
				currentLeadIndex={currentLeadIndex}
				inboxIndex={inboxIndex}
				actionContext={{
					listPersonId,
					prospectId,
					trackerOrganizationId: primarySignal.trackerOrganizationId,
				}}
				isSummaryExpanded={expandedSummaryIds.has(primarySignal.id)}
				organizationName={organizationName}
				prospectName={prospectName}
				signal={primarySignal}
				setCardRef={setCardRef}
				onGenerateDrafts={onGenerateDrafts}
				onReachOut={onReachOut}
				onSelectLead={onSelectLead}
				onToggleSummary={onToggleSummary}
			/>

			{additionalSignals.length > 0 && (
				<Button
					className="!h-auto !rounded-none !border-0 !bg-transparent !px-0 !py-1 hover:!shadow-none"
					color={ButtonColor.ACTION_SECONDARY}
					fontSize={FontSize.XS}
					size={ButtonSize.XS}
					textClassName="text-blue-action hover:underline whitespace-normal"
					title={getToggleSignalsLabel(prospectName, additionalSignals.length, isExpanded)}
					type="button"
					onClick={() => onToggleSignals(inboxIndex)}
				/>
			)}

			{isExpanded &&
				additionalSignals.map((signal) => (
					<SignalCard
						key={`${signal.id}-${inboxIndex}`}
						cardKey={`${signal.id}-${inboxIndex}`}
						currentLeadIndex={currentLeadIndex}
						inboxIndex={inboxIndex}
						actionContext={{
							listPersonId,
							prospectId,
							trackerOrganizationId: signal.trackerOrganizationId,
						}}
						isSummaryExpanded={expandedSummaryIds.has(signal.id)}
						organizationName={organizationName}
						prospectName={prospectName}
						signal={signal}
						setCardRef={setCardRef}
						onGenerateDrafts={onGenerateDrafts}
						onReachOut={onReachOut}
						onSelectLead={onSelectLead}
						onToggleSummary={onToggleSummary}
					/>
				))}
		</div>
	);
};
